import type { ComplaintRow } from '@/api/import';
import type { FilterItem } from '@/components/crud/types';
import { SIM_TODAY } from '@/utils/time';

import { CLOSURE_FILTER_OPTIONS, closureStatusOf } from './complaint-closure';

/** 投诉日期范围 [开始, 结束]，格式 YYYY-MM-DD */
export type ComplaintDateRange = [string, string];

export interface ComplaintRowFilterState {
  q: string;
  dateRange: ComplaintDateRange | null;
  deptP: string;
  deptC: string;
  fzr: string;
  week: string;
  month: string;
  closure: string;
}

export function emptyComplaintRowFilters(): ComplaintRowFilterState {
  return {
    q: '',
    dateRange: null,
    deptP: '',
    deptC: '',
    fzr: '',
    week: '',
    month: '',
    closure: '',
  };
}

function uniqueSorted(values: string[]): string[] {
  const set = new Set<string>();
  values.forEach((value) => {
    const text = String(value || '').trim();
    if (text) set.add(text);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'zh-CN'));
}

/** 周次按数字升序（避免字符串排序 10 排在 2 前） */
function uniqueSortedWeeks(rows: ComplaintRow[]): number[] {
  const set = new Set<number>();
  rows.forEach((row) => {
    if (row.week != null && Number.isFinite(row.week)) {
      set.add(row.week);
    }
  });
  return Array.from(set).sort((a, b) => a - b);
}

function uniqueSortedMonths(rows: ComplaintRow[]): number[] {
  const set = new Set<number>();
  rows.forEach((row) => {
    if (row.month != null && Number.isFinite(row.month)) {
      set.add(row.month);
    }
  });
  return Array.from(set).sort((a, b) => a - b);
}

/** 筛选项配置；下拉选项由当前批次明细动态生成 */
export function buildComplaintRowFilterItems(rows: ComplaintRow[]): FilterItem[] {
  const deptPOptions = uniqueSorted(rows.map((row) => row.deptP));
  const deptCOptions = uniqueSorted(rows.map((row) => row.deptC || ''));
  const fzrOptions = uniqueSorted(rows.map((row) => row.fzr || ''));
  const weekOptions = uniqueSortedWeeks(rows).map((week) => ({
    label: `第 ${week} 周`,
    value: String(week),
  }));
  const monthOptions = uniqueSortedMonths(rows).map((month) => ({
    label: `${month} 月`,
    value: String(month),
  }));

  return [
    {
      key: 'q',
      type: 'input',
      placeholder: '搜索料号、品名、机种、异常、负责人',
      width: '280px',
    },
    {
      key: 'dateRange',
      type: 'daterange',
      startPlaceholder: '投诉开始',
      endPlaceholder: '投诉结束',
      width: '268px',
    },
    {
      key: 'deptC',
      type: 'select',
      placeholder: '全部投诉部门',
      width: '148px',
      options: deptCOptions.map((value) => ({ label: value, value })),
    },
    {
      key: 'deptP',
      type: 'select',
      placeholder: '全部生产部门',
      width: '148px',
      options: deptPOptions.map((value) => ({ label: value, value })),
    },
    {
      key: 'fzr',
      type: 'select',
      placeholder: '全部负责人',
      width: '132px',
      options: fzrOptions.map((value) => ({ label: value, value })),
    },
    {
      key: 'week',
      type: 'select',
      placeholder: '全部周',
      width: '108px',
      options: weekOptions,
    },
    {
      key: 'month',
      type: 'select',
      placeholder: '全部月',
      width: '100px',
      options: monthOptions,
    },
    {
      key: 'closure',
      type: 'select',
      placeholder: '全部闭环状态',
      width: '188px',
      pinned: true,
      clearable: false,
      options: [
        { label: '全部闭环状态', value: '' },
        ...CLOSURE_FILTER_OPTIONS.map((item) => ({ label: item.label, value: item.value })),
      ],
    },
  ];
}

const KEYWORD_FIELDS: (keyof ComplaintRow)[] = [
  'lh',
  'pm',
  'jz',
  'yc',
  'cause',
  'temp',
  'permanent',
  'fzr',
];

function complaintDateYmd(value: string | null): string | null {
  if (!value) return null;
  const matched = /^\d{4}-\d{2}-\d{2}/.exec(String(value).trim());
  return matched ? matched[0] : null;
}

function inComplaintDateRange(date: string | null, range: ComplaintDateRange | null): boolean {
  if (!range || !range[0] || !range[1]) return true;
  const ymd = complaintDateYmd(date);
  if (!ymd) return false;
  return ymd >= range[0] && ymd <= range[1];
}

export function filterComplaintRows(
  rows: ComplaintRow[],
  filters: ComplaintRowFilterState
): ComplaintRow[] {
  const keyword = filters.q.trim().toLowerCase();
  return rows.filter((row) => {
    if (keyword) {
      const haystack = KEYWORD_FIELDS
        .map((key) => row[key])
        .filter((value) => value != null && value !== '')
        .join(' ')
        .toLowerCase();
      if (haystack.indexOf(keyword) === -1) return false;
    }
    if (!inComplaintDateRange(row.date, filters.dateRange)) return false;
    if (filters.deptP && row.deptP !== filters.deptP) return false;
    if (filters.deptC && (row.deptC || '') !== filters.deptC) return false;
    if (filters.fzr && (row.fzr || '') !== filters.fzr) return false;
    if (filters.week && String(row.week ?? '') !== filters.week) return false;
    if (filters.month && String(row.month ?? '') !== filters.month) return false;
    if (filters.closure && closureStatusOf(row, SIM_TODAY) !== filters.closure) return false;
    return true;
  });
}
